import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import {
  ArrowRight,
  MessageSquare,
  AlertTriangle,
  UserCog,
  FileText,
  Flag,
  Timer,
  Trash2,
  UserX,
  Search,
  FileCheck2,
  RotateCcw,
  Building2,
  DoorOpen,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import type { AdminActivityLogEntry } from '@/lib/admin/types';

const ACTION_ICONS: Record<string, typeof ArrowRight> = {
  feedback_status_changed: ArrowRight,
  feedback_resolved: MessageSquare,
  feedback_priority_changed: AlertTriangle,
  note_added: FileText,
  role_changed: UserCog,
  feature_flag_updated: Flag,
  retention_config_updated: Timer,
  retention_run_started: Trash2,
  // Written by the account-deletion function's admin tool (118-09).
  erasure_account_lookup: Search,
  erasure_requested: UserX,
  erasure_expedited: UserX,
  erasure_proof_viewed: FileCheck2,
  erasure_requests_listed: FileCheck2,
  erasure_requeued: RotateCcw,
  // Closed companies (119-06 actions, labelled in 119-07).
  company_lifecycle_viewed: Building2,
  company_data_deletion_requested: Trash2,
  company_reopened: DoorOpen,
  company_data_deletion_requeued: RotateCcw,
};

const ACTION_COLORS: Record<string, string> = {
  feedback_status_changed: '#FF9500',
  feedback_resolved: '#34C759',
  feedback_priority_changed: '#FF3B30',
  note_added: '#00B8B8',
  role_changed: '#007AFF',
  feature_flag_updated: '#5856D6',
  // Retention governs deletion; the on-demand run is destructive and is
  // coloured as such so it stands out when scanning the feed.
  retention_config_updated: '#FF9500',
  retention_run_started: '#FF3B30',
  erasure_account_lookup: '#8E8E93',
  erasure_requested: '#FF3B30',
  erasure_expedited: '#FF3B30',
  erasure_proof_viewed: '#8E8E93',
  erasure_requests_listed: '#8E8E93',
  erasure_requeued: '#FF9500',
  company_lifecycle_viewed: '#8E8E93',
  company_data_deletion_requested: '#FF3B30',
  company_reopened: '#007AFF',
  company_data_deletion_requeued: '#FF9500',
};

function formatActionLabel(entry: AdminActivityLogEntry): string {
  const details = entry.details as Record<string, string>;
  switch (entry.action) {
    case 'feedback_status_changed':
      return `Status: ${details.from} → ${details.to}`;
    case 'feedback_resolved':
      return 'Feedback resolved';
    case 'feedback_priority_changed':
      return `Priority: ${details.from} → ${details.to}`;
    case 'note_added':
      return 'Added a note';
    case 'role_changed':
      return `Role set to ${details.role}`;
    case 'feature_flag_updated':
      return `Flag “${details.key}” ${details.enabled ? 'enabled' : 'disabled'}`;
    case 'retention_config_updated':
      return `Retention for ${details.key}: ${details.enabled ? `${details.retention_days} days` : 'off'}`;
    case 'retention_run_started':
      return 'Ran data retention';
    case 'erasure_account_lookup':
      return 'Looked up an account for erasure';
    case 'erasure_requested':
      return details.immediate ? 'Filed an immediate erasure' : 'Filed an erasure (30 days)';
    case 'erasure_expedited':
      return 'Expedited an erasure';
    case 'erasure_proof_viewed':
      return 'Viewed an erasure proof';
    case 'erasure_requests_listed':
      return 'Viewed the erasure queue';
    case 'erasure_requeued':
      return 'Re-queued a failed erasure';
    case 'company_lifecycle_viewed':
      return 'Looked up a closed company';
    case 'company_data_deletion_requested':
      return details.reason === 'verified_founder_request'
        ? "Filed a company-data deletion (founder's request)"
        : details.reason === 'admin_reviewed_request'
          ? 'Filed a company-data deletion (reviewed request)'
          : 'Filed a company-data deletion';
    case 'company_reopened':
      return 'Reopened a closed company';
    case 'company_data_deletion_requeued':
      return 'Re-queued a failed company-data deletion';
    default:
      return entry.action.replace(/_/g, ' ');
  }
}

function formatTimeAgo(dateString: string): string {
  const now = Date.now();
  const diff = now - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

interface AdminActivityLogProps {
  entries: AdminActivityLogEntry[];
  maxHeight?: number;
}

export function AdminActivityLog({ entries, maxHeight = 240 }: AdminActivityLogProps) {
  const colors = useThemeColors();

  if (entries.length === 0) {
    return (
      <View className="py-4 items-center">
        <Text style={{ color: colors.text.tertiary }} className="text-xs">
          No recent activity
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={{ maxHeight }} showsVerticalScrollIndicator={false}>
      {entries.map((entry, i) => {
        const Icon = ACTION_ICONS[entry.action] ?? ArrowRight;
        const iconColor = ACTION_COLORS[entry.action] ?? colors.text.tertiary;

        return (
          <View
            key={entry.id}
            className="flex-row items-start gap-3 py-2.5"
            style={i < entries.length - 1 ? {
              borderBottomWidth: 1,
              borderBottomColor: colors.border.light,
            } : undefined}
          >
            <View
              className="w-7 h-7 rounded-lg items-center justify-center mt-0.5"
              style={{ backgroundColor: `${iconColor}15` }}
            >
              <Icon size={13} color={iconColor} />
            </View>
            <View className="flex-1">
              <Text style={{ color: colors.text.primary }} className="text-xs font-medium">
                {formatActionLabel(entry)}
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-[10px] mt-0.5">
                {entry.admin_email} · {formatTimeAgo(entry.created_at)}
              </Text>
            </View>
          </View>
        );
      })}
    </ScrollView>
  );
}
